import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface PageRefreshContextType {
  isRefreshing: boolean;
  refresh: () => Promise<void>;
  registerHandler: (handler: () => void | Promise<void>) => () => void;
}

const PageRefreshContext = createContext<PageRefreshContextType | undefined>(undefined);

/**
 * Provides a page-level refresh button shared by the app shell. Pages may
 * register a custom in-place refresh handler via {@link useRegisterPageRefresh};
 * if none is registered when the user clicks the header refresh button, we
 * fall back to a soft router reload that stays on the current URL.
 */
export function PageRefreshProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<(() => void | Promise<void>) | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  const registerHandler = useCallback((handler: () => void | Promise<void>) => {
    handlerRef.current = handler;
    return () => {
      if (handlerRef.current === handler) handlerRef.current = null;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (handlerRef.current) {
        await handlerRef.current();
      } else {
        // Soft reload — keep the URL, re-mount the route so data hooks re-fetch.
        navigate(0);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, navigate]);

  return (
    <PageRefreshContext.Provider value={{ isRefreshing, refresh, registerHandler }}>
      {children}
    </PageRefreshContext.Provider>
  );
}

/**
 * Reads the page-refresh state. Used by the header button.
 */
export function usePageRefresh(): PageRefreshContextType {
  const ctx = useContext(PageRefreshContext);
  if (!ctx) throw new Error('usePageRefresh must be used within PageRefreshProvider');
  return ctx;
}

/**
 * Register a page-specific refresh handler. The handler runs when the user
 * clicks the header refresh button. The registration is automatically torn
 * down on unmount so the next page's handler takes over.
 *
 * @param handler - Async or sync callback that re-fetches the current page's data
 */
export function useRegisterPageRefresh(handler: () => void | Promise<void>) {
  const { registerHandler } = usePageRefresh();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return registerHandler(() => handlerRef.current());
  }, [registerHandler]);
}
